import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HiUsers } from 'react-icons/hi';
import { ChannelListItem } from '../../../types';
import { useGeneral } from '../../../contexts';
import Button from '../../ui/Button/Button';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import { createSlug } from '../../../utils/urlSlug';
import styles from './CommunityCard.module.css';

interface CommunityCardProps {
  community: ChannelListItem;
  isJoined: boolean;
  onJoin?: (channelID: string) => void;
  onLeave?: (channelID: string) => void;
  onClick?: (community: ChannelListItem) => void;
  showParticipantCount?: boolean;
  disableHoverLift?: boolean;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  community,
  isJoined,
  onJoin,
  onLeave,
  onClick,
  showParticipantCount = true,
  disableHoverLift = false
}) => {
  const navigate = useNavigate();
  const { language } = useGeneral();
  const { getTranslation } = language;
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const logoUrl = community.logo || community.channelLogo || null;
  const hasLogo = !!logoUrl;
  const [imageLoading, setImageLoading] = useState(hasLogo);
  const channelName = community.channelTitle || community.title;
  const memberCount = community.channelUserCount || community.userCount || community.user_count || community.memberCount || 0;

  const handleJoinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmDialog(true);
  };

  const handleConfirm = () => {
    setShowConfirmDialog(false);
    if (isJoined && onLeave) {
      onLeave(community.channelID);
    } else if (!isJoined && onJoin) {
      onJoin(community.channelID);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(community);
    }
  };

  const isAdmin = String(community.isAdmin) === "1";

  const handleAdminPanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const channelTitle = community.channelTitle || community.title;
    if (!channelTitle) return;
    const slug = createSlug(channelTitle);
    navigate(`/communities/${slug}/admin`);
  };

  return (
    <div
      className={`${styles.communityCard} ${disableHoverLift ? styles.noHoverLift : ''}`}
      onClick={handleCardClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Participant Count - Top Right */}
      {showParticipantCount && (
        <div className={styles.participantCount}>
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiUsers size={14} />
          <span>{memberCount}</span>
        </div>
      )}

      <div className={styles.top}>
        <div className={styles.iconContainer}>
          {hasLogo ? (
            <>
              {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
              <img 
                src={logoUrl} 
                alt={community.title}
                className={styles.icon}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageLoading(false);
                }}
              />
            </>
          ) : (
            <ChannelInitialIcon channelName={channelName} className={styles.icon} />
          )}
        </div>
      </div>
      
      <div className={styles.body}>
        <h4 className={styles.title}>{community.channelTitle || community.title}</h4>
        <p className={styles.description}>{community.description}</p>
      </div>
      
      {isAdmin ? (
        <Button
          variant="orange"
          size="small"
          onClick={handleAdminPanelClick}
          className={styles.joinButton}
        >
          {getTranslation('communities.manage') || 'Yönet'}
        </Button>
      ) : (
        <Button
          variant={isJoined ? 'success' : 'primary'}
          size="small"
          onClick={handleJoinClick}
          className={styles.joinButton}
        >
          {isJoined ? getTranslation('communities.joined') : getTranslation('communities.joinCommunity')}
        </Button>
      )}

      {/* Confirmation Dialog - rendered via portal to avoid stacking context issues */}
      {showConfirmDialog && createPortal(
        <div className={styles.modalOverlay} onClick={handleCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {isJoined ? getTranslation('communities.confirmLeave') : getTranslation('communities.confirmJoin')}
            </h3>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="large"
                onClick={handleCancel}
                className={styles.modalButton}
              >
                {getTranslation('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="large"
                onClick={handleConfirm}
                className={styles.modalButton}
              >
                {isJoined ? getTranslation('communities.leaveCommunity') : getTranslation('communities.joinCommunity')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CommunityCard;

